import { Box, Fade, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { BarPlot } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { LinePlot } from '@mui/x-charts/LineChart';
import { ChartsContainer } from '@mui/x-charts/ChartsContainer';
import { LazyMountOnVisible } from '../../../../../components/utility/LazyMountOnVisible';
import type { RecurringRevenueReport } from '@vy/types';
import { DEFAULT_CURRENCY, type Currency } from '@vy/types';
import { useCallback, useMemo, useState } from 'react';
import { formatNumberAbbreviated } from '../../../../../utils/number';
import {
  REPORT_CARD_SX,
  REPORT_CHART_AXIS_LABEL_STYLE,
  REPORT_CHART_TICK_LABEL_STYLE,
} from '../../../reports-components/chart-theme';
import { ReportChartLegend } from '../../../reports-components/ReportChartLegend';
import { reportToggleGroupSx } from '../../../reports-components/report-toolbar-styles';
import { RecurringRevenueReportChartTooltip } from './RecurringRevenueReportChartTooltip';
import {
  CHART_COLORS,
  CHART_SERIES_IDS,
  SHOW_NET_NEW_MRR_LINE,
} from './recurring-revenue-report-chart-consts';
import type {
  ChartPeriodMode,
  ChartViewMode,
  WaterfallChartData,
} from '../../utils/chart-movements';
import { useRecurringRevenueReportChartData } from '../../hooks/use-recurring-revenue-report-chart-data';

type RecurringRevenueReportChartProps = {
  // Pre-filtered report from the worker bundle (filters already applied).
  filteredReport: RecurringRevenueReport;
  fromDate?: Date | null;
  toDate?: Date | null;
  periodMode: ChartPeriodMode;
  onPeriodModeChange: (mode: ChartPeriodMode) => void;
  searchText?: string;
};

const TRANSPARENT = 'rgba(0,0,0,0)';

export const RecurringRevenueReportChart: React.FC<RecurringRevenueReportChartProps> = ({
  filteredReport,
  fromDate,
  toDate,
  periodMode,
  onPeriodModeChange,
  searchText,
}) => {
  const [viewMode, setViewMode] = useState<ChartViewMode>('breakdown');

  const chartCurrency = useMemo((): Currency => {
    const currencies = new Set(filteredReport.map((row) => row.originalCurrency as Currency));
    return currencies.size === 1 ? [...currencies][0] : DEFAULT_CURRENCY;
  }, [filteredReport]);

  const chartResult = useRecurringRevenueReportChartData({
    filteredReport,
    fromDate,
    toDate,
    periodMode,
    viewMode,
    searchText,
  });

  const handlePeriodChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, value: ChartPeriodMode | null) => {
      if (value) {
        onPeriodModeChange(value);
      }
    },
    [onPeriodModeChange],
  );

  const handleViewModeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, value: ChartViewMode | null) => {
      if (value) {
        setViewMode(value);
      }
    },
    [],
  );

  const isEmpty =
    chartResult.mode === 'timeseries' ? chartResult.data.length === 0 : false;

  return (
    <Box sx={{ px: 2, pb: '20px' }}>
      <Fade in timeout={400}>
        <Box sx={REPORT_CARD_SX}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: 40
            }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={reportToggleGroupSx(40)}
            >
              <ToggleButton value="breakdown">Breakdown</ToggleButton>
              <ToggleButton value="bridge">Bridge</ToggleButton>
            </ToggleButtonGroup>
            {viewMode === 'breakdown' && (
              <ToggleButtonGroup
                value={periodMode}
                exclusive
                onChange={handlePeriodChange}
                size="small"
                sx={reportToggleGroupSx(40)}
              >
                <ToggleButton value="monthly">Monthly</ToggleButton>
                <ToggleButton value="quarterly">Quarterly</ToggleButton>
                <ToggleButton value="annual">Annual</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>

          {isEmpty ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 150
              }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                No MRR movement data for the selected filters
              </Typography>
            </Box>
          ) : chartResult.mode === 'waterfall' ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: '56px'
              }}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0
                }}>
                <LazyMountOnVisible minHeight={257}>
                  <WaterfallChart data={chartResult.data} currency={chartCurrency} />
                </LazyMountOnVisible>
              </Box>
              <Box sx={{ pt: 1 }}>
                <ReportChartLegend items={WATERFALL_LEGEND_ITEMS} orientation="vertical" />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: '56px'
              }}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0
                }}>
                <LazyMountOnVisible minHeight={257}>
                  <TimeSeriesChart data={chartResult.data} currency={chartCurrency} />
                </LazyMountOnVisible>
              </Box>
              <Box sx={{ pt: 1 }}>
                <ReportChartLegend items={TIMESERIES_LEGEND_ITEMS} orientation="vertical" />
              </Box>
            </Box>
          )}
        </Box>
      </Fade>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Time-series (multi-period) chart
// ---------------------------------------------------------------------------

type TimeSeriesPoint = {
  label: string;
  new: number;
  expansion: number;
  reactivation: number;
  contraction: number;
  churn: number;
  netNewMrr: number;
  beginningMrr: number;
  endingMrr: number;
};

type TimeSeriesChartProps = {
  data: TimeSeriesPoint[];
  currency: Currency;
};

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({ data, currency }) => {
  const labels = data.map((d) => d.label);
  const mrrData = data.map((d) => d.beginningMrr);
  const newData = data.map((d) => d.new);
  const expansionData = data.map((d) => d.expansion);
  const reactivationData = data.map((d) => d.reactivation);
  // Contraction/churn are stored as negative magnitudes in the pipeline.
  // We render them as positive heights inside the negatives stack, which
  // sits side-by-side with the positives stack. Tooltip still shows them
  // with a minus sign.
  const contractionData = data.map((d) => Math.abs(d.contraction));
  const churnData = data.map((d) => Math.abs(d.churn));
  // Positives stack: spacer = beginningMrr, then new/expansion/reactivation
  // climb above the top of the MRR base.
  const positivesSpacerData = mrrData;
  // Negatives stack: spacer = beginningMrr − (|contraction| + |churn|), then
  // losses fill back up to beginningMrr, visually "notching" down from the
  // top of the MRR base.
  const negativesSpacerData = data.map((_, i) =>
    Math.max(0, mrrData[i] - contractionData[i] - churnData[i]),
  );
  const netNewMrrData = data.map((d) => d.netNewMrr);

  return (
    <ChartsContainer
      height={257}
      margin={{ left: 65, top: 10, right: 20, bottom: 30 }}
      sx={timeseriesChartSx}
      xAxis={[
        {
          id: 'periods',
          scaleType: 'band',
          data: labels,
          tickLabelStyle: tickLabelStyle,
        },
      ]}
      yAxis={[
        {
          id: 'movements',
          valueFormatter: (value: number) => formatNumberAbbreviated(value),
          tickLabelStyle: axisLabelStyle,
        },
      ]}
      series={[
        // MRR base — wide gray bar per period (its own side-by-side slot).
        {
          id: CHART_SERIES_IDS.Mrr,
          type: 'bar',
          stack: 'mrr',
          yAxisId: 'movements',
          data: mrrData,
          label: 'MRR',
          color: CHART_COLORS.mrr,
        },
        // Positives stack — single side-by-side slot. Spacer pushes the
        // visible bars up to the top of the MRR base, then new/expansion/
        // reactivation climb above it.
        {
          id: 'positives-spacer',
          type: 'bar',
          stack: 'positives',
          yAxisId: 'movements',
          data: positivesSpacerData,
          label: '',
          color: TRANSPARENT,
        },
        {
          id: CHART_SERIES_IDS.New,
          type: 'bar',
          stack: 'positives',
          yAxisId: 'movements',
          data: newData,
          label: 'New',
          color: CHART_COLORS.new,
        },
        {
          id: CHART_SERIES_IDS.Expansion,
          type: 'bar',
          stack: 'positives',
          yAxisId: 'movements',
          data: expansionData,
          label: 'Expansion',
          color: CHART_COLORS.expansion,
        },
        {
          id: CHART_SERIES_IDS.Reactivation,
          type: 'bar',
          stack: 'positives',
          yAxisId: 'movements',
          data: reactivationData,
          label: 'Reactivation',
          color: CHART_COLORS.reactivation,
        },
        // Negatives stack — single side-by-side slot. Spacer leaves a gap
        // from 0 to `beginning − totalLosses`, then contraction/churn fill
        // back up to `beginning`, extending the stack DOWNWARD from the top
        // of the MRR base.
        {
          id: 'negatives-spacer',
          type: 'bar',
          stack: 'negatives',
          yAxisId: 'movements',
          data: negativesSpacerData,
          label: '',
          color: TRANSPARENT,
        },
        {
          id: CHART_SERIES_IDS.Contraction,
          type: 'bar',
          stack: 'negatives',
          yAxisId: 'movements',
          data: contractionData,
          label: 'Contraction',
          color: CHART_COLORS.contraction,
        },
        {
          id: CHART_SERIES_IDS.Churn,
          type: 'bar',
          stack: 'negatives',
          yAxisId: 'movements',
          data: churnData,
          label: 'Churn',
          color: CHART_COLORS.churn,
        },
        ...(SHOW_NET_NEW_MRR_LINE
          ? [
              {
                id: 'netNewMrr',
                type: 'line' as const,
                yAxisId: 'movements',
                data: netNewMrrData,
                label: 'Net New',
                color: '#1B3D55',
                showMark: false,
              },
            ]
          : []),
      ]}
    >
      <ChartsGrid horizontal />
      <BarPlot borderRadius={4} skipAnimation />
      <ChartsReferenceLine y={0} lineStyle={{ stroke: '#CBD5E0', strokeWidth: 1 }} />
      {SHOW_NET_NEW_MRR_LINE && <LinePlot skipAnimation />}
      {/* tickSize=0: frees the label height budget so Inter's taller glyphs
          aren't ellipsized to empty (tick marks are hidden via sx anyway). */}
      <ChartsXAxis axisId="periods" tickSize={0} />
      <ChartsYAxis axisId="movements" />
      <RecurringRevenueReportChartTooltip currency={currency} mode="timeseries" />
    </ChartsContainer>
  );
};

// ---------------------------------------------------------------------------
// Single-period waterfall chart
// ---------------------------------------------------------------------------

type WaterfallChartProps = {
  data: WaterfallChartData;
  currency: Currency;
};

const WATERFALL_CATEGORIES = [
  'Beginning',
  'New',
  'Expansion',
  'Reactivation',
  'Contraction',
  'Churn',
  'Ending',
] as const;

const WaterfallChart: React.FC<WaterfallChartProps> = ({ data, currency }) => {
  const beginning = data.beginning;
  const contractionAbs = Math.abs(data.contraction);
  const churnAbs = Math.abs(data.churn);

  // Running totals at each category boundary
  const afterNew = beginning + data.new;
  const afterExpansion = afterNew + data.expansion;
  const afterReactivation = afterExpansion + data.reactivation;
  const afterContraction = afterReactivation + data.contraction; // data.contraction negative
  const afterChurn = afterContraction + data.churn; // data.churn negative

  // Transparent offset values that position each colored bar correctly.
  // For contraction/churn: offset sits at the lower running total, the colored
  // bar rises by |amount| back up to the prior running total.
  const offsetData = [
    0,                     // Beginning — starts at 0
    beginning,             // New — stacks on top of Beginning
    afterNew,              // Expansion — stacks on running total after New
    afterExpansion,        // Reactivation
    afterContraction,      // Contraction — starts at the lower point
    afterChurn,            // Churn — starts at the lower point
    0,                     // Ending — starts at 0
  ];

  const beginningData =    [beginning, 0, 0, 0, 0, 0, 0];
  const newData =          [0, data.new, 0, 0, 0, 0, 0];
  const expansionData =    [0, 0, data.expansion, 0, 0, 0, 0];
  const reactivationData = [0, 0, 0, data.reactivation, 0, 0, 0];
  const contractionData =  [0, 0, 0, 0, contractionAbs, 0, 0];
  const churnData =        [0, 0, 0, 0, 0, churnAbs, 0];
  const endingData =       [0, 0, 0, 0, 0, 0, data.ending];

  return (
    <ChartsContainer
      height={257}
      margin={{ left: 65, top: 30, right: 20, bottom: 30 }}
      sx={chartSx}
      xAxis={[
        {
          id: 'waterfall-categories',
          scaleType: 'band',
          data: WATERFALL_CATEGORIES as unknown as string[],
          tickLabelStyle: tickLabelStyle,
        },
      ]}
      yAxis={[
        {
          id: 'movements',
          valueFormatter: (value: number) => formatNumberAbbreviated(value),
          tickLabelStyle: axisLabelStyle,
        },
      ]}
      series={[
        {
          id: 'waterfall-offset',
          type: 'bar',
          stack: 'waterfall',
          yAxisId: 'movements',
          data: offsetData,
          label: '',
          color: TRANSPARENT,
        },
        {
          id: CHART_SERIES_IDS.Beginning,
          type: 'bar',
          stack: 'waterfall',
          yAxisId: 'movements',
          data: beginningData,
          label: 'Beginning',
          color: CHART_COLORS.beginning,
        },
        {
          id: CHART_SERIES_IDS.New,
          type: 'bar',
          stack: 'waterfall',
          yAxisId: 'movements',
          data: newData,
          label: 'New',
          color: CHART_COLORS.new,
        },
        {
          id: CHART_SERIES_IDS.Expansion,
          type: 'bar',
          stack: 'waterfall',
          yAxisId: 'movements',
          data: expansionData,
          label: 'Expansion',
          color: CHART_COLORS.expansion,
        },
        {
          id: CHART_SERIES_IDS.Reactivation,
          type: 'bar',
          stack: 'waterfall',
          yAxisId: 'movements',
          data: reactivationData,
          label: 'Reactivation',
          color: CHART_COLORS.reactivation,
        },
        {
          id: CHART_SERIES_IDS.Contraction,
          type: 'bar',
          stack: 'waterfall',
          yAxisId: 'movements',
          data: contractionData,
          label: 'Contraction',
          color: CHART_COLORS.contraction,
        },
        {
          id: CHART_SERIES_IDS.Churn,
          type: 'bar',
          stack: 'waterfall',
          yAxisId: 'movements',
          data: churnData,
          label: 'Churn',
          color: CHART_COLORS.churn,
        },
        {
          id: CHART_SERIES_IDS.Ending,
          type: 'bar',
          stack: 'waterfall',
          yAxisId: 'movements',
          data: endingData,
          label: 'Ending',
          color: CHART_COLORS.ending,
        },
      ]}
    >
      <ChartsGrid horizontal />
      <BarPlot borderRadius={4} skipAnimation />
      <ChartsReferenceLine y={0} lineStyle={{ stroke: '#CBD5E0', strokeWidth: 1 }} />
      <ChartsXAxis axisId="waterfall-categories" tickSize={0} />
      <ChartsYAxis axisId="movements" />
      <RecurringRevenueReportChartTooltip currency={currency} mode="waterfall" waterfall={data} />
    </ChartsContainer>
  );
};

// ---------------------------------------------------------------------------
// Shared chart styles
// ---------------------------------------------------------------------------

const chartSx = {
  [`.${axisClasses.root}`]: {
    [`.${axisClasses.tick}, .${axisClasses.line}`]: {
      stroke: 'none',
    },
  },
} as const;

// Time-series chart uses "floating" movement stacks positioned above a
// transparent spacer. MUI's native borderRadius rounds the TOP of the topmost
// positive segment in each stack (reactivation, churn, mrr). The BOTTOM of
// each floating stack sits on a transparent spacer, so MUI treats it as an
// internal joint and leaves it square. We restore rounded bottom corners
// only on the bottommost visible segment of each stack — `new` (bottom of
// positives) and `contraction` (bottom of negatives). Internal joints
// between new/expansion/reactivation and contraction/churn stay flat.
const timeseriesChartSx = {
  ...chartSx,
  '& .MuiBarElement-series-new, & .MuiBarElement-series-contraction': {
    // inset(offset round TL TR BR BL) — only bottom corners rounded.
    clipPath: 'inset(0 round 0 0 4px 4px)',
  },
} as const;

const tickLabelStyle = REPORT_CHART_TICK_LABEL_STYLE;
const axisLabelStyle = REPORT_CHART_AXIS_LABEL_STYLE;

const TIMESERIES_LEGEND_ITEMS = [
  { label: 'MRR', color: CHART_COLORS.mrr },
  { label: 'New', color: CHART_COLORS.new },
  { label: 'Expansion', color: CHART_COLORS.expansion },
  { label: 'Reactivation', color: CHART_COLORS.reactivation },
  { label: 'Contraction', color: CHART_COLORS.contraction },
  { label: 'Churn', color: CHART_COLORS.churn },
];

const WATERFALL_LEGEND_ITEMS = [
  { label: 'Beginning', color: CHART_COLORS.beginning },
  { label: 'New', color: CHART_COLORS.new },
  { label: 'Expansion', color: CHART_COLORS.expansion },
  { label: 'Reactivation', color: CHART_COLORS.reactivation },
  { label: 'Contraction', color: CHART_COLORS.contraction },
  { label: 'Churn', color: CHART_COLORS.churn },
  { label: 'Ending', color: CHART_COLORS.ending },
];
